import { ApolloClient } from '@apollo/client';
import type { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import * as Sentry from '@sentry/gatsby';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import fetch from 'cross-fetch';
import { print } from 'graphql';
import type { JwtPayload } from 'jwt-decode';
import jwtDecode from 'jwt-decode';

import { COOKIE_KEY_ACCESS, COOKIE_KEY_ID } from '@constants/cookie-key';
import { isProduction } from '@constants/env';

import { tokenStorage } from '@utils/storage';
import { stripEmptyStrings } from '@utils/strip';

import {
  RefreshTokenDocument,
  type RefreshTokenMutation,
} from 'generated/session.generated';

interface CreateApolloClientOptions {
  graphqlEndpoint?: string;
}

const generateLink = (options?: CreateApolloClientOptions) => {
  const endpoint =
    options?.graphqlEndpoint ?? process.env.GATSBY_GRAPHQL_ENDPOINT ?? '';
  const errorLink = onError(
    ({ graphQLErrors, networkError, forward, operation }) => {
      // Handle backend session expired
      if (graphQLErrors) {
        graphQLErrors.forEach(async (error) => {
          const { message, extensions, code } = error as any;

          // eslint-disable-next-line no-console
          console.log(`[GraphQL error]: ${message}`);

          if ((extensions?.code ?? code) === 'UNAUTHENTICATED') {
            tokenStorage.removeItem(COOKIE_KEY_ID);
            tokenStorage.removeItem(COOKIE_KEY_ACCESS);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const oldHeaders = operation.getContext().headers;
            delete oldHeaders['authorization'];
            operation.setContext({
              headers: oldHeaders,
            });

            return forward(operation);
          } else {
            // Add scoped report details and send to Sentry
            const operationDefinition: any = operation.query.definitions.find(
              (d) => Boolean(d)
            );
            Sentry.withScope((scope: Sentry.Scope) => {
              scope.setTag('Service', 'Frontend');
              // Annotate whether failing operation was query/mutation/subscription
              if (operationDefinition) {
                scope.setTag('kind', operationDefinition.operation);
                scope.setTag('name', operationDefinition.name.value);
              }

              // Log query and variables as extras (make sure to strip out sensitive data!)
              scope.setExtra('query', operation.query.loc?.source);
              scope.setExtra('variables', operation.variables);

              Sentry.captureException(new Error(error.message));
            });

            await Sentry.flush(2000);
            return undefined;
          }
        });
      }

      if (networkError && !isProduction) {
        // eslint-disable-next-line no-console
        console.log(`[Network error]: ${networkError.message}`);
      }
    }
  );

  const authLink = setContext((_, { headers = {} }) => {
    const idToken = tokenStorage.getItem(COOKIE_KEY_ID);
    if (!headers['authorization'] && idToken) {
      headers['authorization'] = idToken;
    }
    return {
      headers,
    };
  });

  const refreshLink = new TokenRefreshLink<
    RefreshTokenMutation['refreshToken']
  >({
    // Field in response body where access token can be found - e.g. response.data.refreshToken contains `id` and `access`
    // Link auto-checks searches into response.data
    accessTokenField: 'refreshToken',
    /** Indicates the current state of access token expiration. If the token is not yet expired or the user does not require a token (guest), then true should be returned */
    isTokenValidOrUndefined: async () => {
      const accessToken = tokenStorage.getItem(COOKIE_KEY_ID);
      if (!accessToken) {
        return true;
      }
      try {
        const { exp } = jwtDecode<JwtPayload>(accessToken);
        if (exp) {
          const condition = exp * 1000 > Date.now();
          return condition;
        }
        return false;
      } catch {
        return false;
      }
    },
    /** Function covers fetch call with request fresh access token */
    fetchAccessToken: async () => {
      const response = await fetch(endpoint, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          query: print(RefreshTokenDocument),
          operationName: 'refreshToken',
        }),
      });
      return response;
    },
    /** Callback which receives a fresh token from Response. From here we can save token to the storage */
    handleFetch: (response) => {
      const access = response.access;
      const id = response.id;

      if (access) {
        tokenStorage.setItem(COOKIE_KEY_ACCESS, access);
      } else {
        tokenStorage.removeItem(COOKIE_KEY_ACCESS);
      }
      if (id) {
        tokenStorage.setItem(COOKIE_KEY_ID, id);
      } else {
        tokenStorage.removeItem(COOKIE_KEY_ID);
      }
      return;
    },
    /** Token fetch error callback. Allows to run additional actions like logout. Don't forget to handle Error if you are using this option */
    handleError: (error) => {
      // eslint-disable-next-line no-console
      console.warn(
        'Your refresh token is invalid. Try to relogin. Message:',
        error.message
      );
    },
  });

  const httpLink = createHttpLink({
    // TODO Once CORS Access-Control-Allow-Origin header is set to non-wildcard, enable this
    credentials: 'include',
    fetch: (input, init) => {
      // Strip empty strings from request payload
      let body = init?.body;
      if (init && body) {
        if (typeof body === 'string') {
          body = JSON.parse(body);
        }
        body = JSON.stringify(stripEmptyStrings(body, { replace: null }));
      }
      return fetch(input, init ? { ...init, body } : undefined);
    },
    uri: endpoint,
  });
  return errorLink.concat(refreshLink.concat(authLink.concat(httpLink)));
};

const createApolloClient = (
  cache: InMemoryCache,
  options?: CreateApolloClientOptions
) =>
  new ApolloClient({
    link: generateLink(options),
    cache,
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
      watchQuery: {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
      },
    },
    devtools: {
      enabled: process.env.GATSBY_BUG_REPORTING_ENABLED === 'true',
    },
  });

export { createApolloClient };
